/**
 * Precision Purchase Options Module
 * ==================================
 * 
 * Handles one-time vs subscription purchase selection and displays TOTAL prices
 * (unit price × quantity) in each purchase option.
 * 
 * @module components/precision-purchase-options
 * 
 * ## Business Logic
 * 
 * | Option | Total Calculation |
 * |--------|-------------------|
 * | One-time | (Unit price - quantity discount) × quantity |
 * | Subscription | Subscription unit price × quantity |
 * 
 * ## Events (set up at module load time)
 * 
 * - Listens: `quantity:changed` - Updates total prices when quantity changes
 * - Dispatches: `selling-plan:changed` - Fired when user selects a purchase type
 * 
 * ## HTML Data Attributes
 * 
 * Container (`[data-precision-purchase-options]`):
 * - `data-one-time-price` - Base one-time unit price in cents
 * - `data-money-format` - Shopify money format
 */

import { formatMoney } from '../utils/format-money.js';

// Capture the `selling_plan` URL param at module-evaluation time — as early as our
// code runs — before the subscription app's init rewrites the URL and strips it.
// Used to pre-select the matching subscription pill on load (refresh/share persistence).
const INITIAL_SELLING_PLAN = (() => {
  try {
    return new URLSearchParams(window.location.search).get('selling_plan') || '';
  } catch (_e) {
    return '';
  }
})();

// Landing with `?purchase=one-time` forces the One-Time option instead of the
// subscription default. Captured early, same as the selling_plan param above.
const FORCE_ONE_TIME = (() => {
  try {
    return new URLSearchParams(window.location.search).get('purchase') === 'one-time';
  } catch (_e) {
    return false;
  }
})();

// Module state (shared between event listener and init)
let containerRef = null;
let isInitialized = false;
let moduleState = {
  currentQuantity: 1,
  quantityDiscount: 0
};

// ============================================================
// Event Listeners - Set up at module load time
// ============================================================

/**
 * Listen for quantity changes from preset quantity module
 * Updates total prices in purchase options
 */
document.addEventListener('quantity:changed', function(e) {
  if (!isInitialized || !containerRef) return;
  if (!e.detail) return;

  // Update module state
  moduleState.currentQuantity = e.detail.quantity || 1;
  moduleState.quantityDiscount = e.detail.quantityDiscount || 0;

  // Update option displays
  if (typeof containerRef.updateOptionDisplays === 'function') {
    containerRef.updateOptionDisplays();
  }
});

// ============================================================
// Initialization
// ============================================================

/**
 * Initialize purchase options functionality
 * 
 * Sets up radio button handlers, frequency dropdown, and manages
 * total price display for each purchase option.
 */
export function initPurchaseOptions() {
  const container = document.querySelector('[data-precision-purchase-options]');
  if (!container) return;

  // Store reference for event listener
  containerRef = container;

  const options = container.querySelectorAll('[data-purchase-option]');
  const radios = container.querySelectorAll('[data-purchase-radio]');
  const sellingPlanInput = document.querySelector('[data-selling-plan-input]');
  const frequencySelect = container.querySelector('[data-frequency-select]');

  // Option 3 — per-plan helper text. Map is a list of {match, text}; `match` is a plan id or plan
  // name the merchant typed in the plan_helper metaobject. Empty/absent → helper stays on its default.
  const helperEl = container.querySelector('[data-subscription-helper]');
  const helperDefault = helperEl ? (helperEl.dataset.helperDefault || helperEl.textContent.trim()) : '';
  const helperMap = (() => {
    const el = container.querySelector('[data-subscription-helper-map]');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent) || [];
    } catch (_e) {
      return [];
    }
  })();

  /**
   * Update the helper paragraph to the text for the currently selected plan, or the default when
   * no plan is matched (or a one-time purchase is selected).
   * ponytail: matches plan_match against the selected option's id OR visible name — merchant can use
   * either. Tighten to id-only if a name ever collides.
   */
  function updateHelperText() {
    if (!helperEl) return;
    let text = helperDefault;
    const selectedRadio = container.querySelector('[data-purchase-radio]:checked');
    const isSubscription = selectedRadio && selectedRadio.dataset.sellingPlan;
    if (isSubscription && helperMap.length && frequencySelect) {
      const opt = frequencySelect.options[frequencySelect.selectedIndex];
      const planId = opt ? opt.value : '';
      const planName = opt ? opt.textContent.trim() : '';
      const hit = helperMap.find((h) => h && (String(h.match) === planId || String(h.match) === planName));
      if (hit && hit.text) text = hit.text;
    }
    helperEl.textContent = text;
  }

  // Get price data from data attributes (mutable: updated on variant change via setVariantData)
  let oneTimeUnitPrice = parseInt(container.dataset.oneTimePrice) || 0;
  let compareAtPrice = parseInt(container.dataset.compareAtPrice) || 0;
  // Regular (non-special) variant price — drives the subscription pill's compare price and
  // discount. special_price_ui applies to the One-Time option ONLY, never to subscriptions.
  let regularUnitPrice = parseInt(container.dataset.regularPrice) || oneTimeUnitPrice;
  const moneyFormat = container.dataset.moneyFormat || '${{amount}}';

  /**
   * Get the selected selling plan ID
   */
  function getSelectedSellingPlan() {
    const selectedRadio = container.querySelector('[data-purchase-radio]:checked');
    if (!selectedRadio) return '';

    if (selectedRadio.dataset.sellingPlan && frequencySelect) {
      return frequencySelect.value;
    }

    return selectedRadio.dataset.sellingPlan || '';
  }

  /**
   * Get subscription price data from frequency selector or subscription option
   */
  function getSubscriptionPriceData() {
    if (frequencySelect) {
      const selectedOption = frequencySelect.options[frequencySelect.selectedIndex];
      if (selectedOption && selectedOption.dataset.planPrice) {
        return {
          unitPrice: parseInt(selectedOption.dataset.planPrice),
          discountPercentage: parseInt(selectedOption.dataset.discountPercentage) || 0
        };
      }
    }

    const subscriptionOption = container.querySelector('[data-purchase-option="subscription"]');
    if (subscriptionOption && subscriptionOption.dataset.planPrice) {
      return {
        unitPrice: parseInt(subscriptionOption.dataset.planPrice),
        discountPercentage: parseInt(subscriptionOption.dataset.discountPercentage) || 0
      };
    }

    return {
      unitPrice: oneTimeUnitPrice,
      discountPercentage: 0
    };
  }

  /**
   * Calculate one-time total with quantity discount
   */
  function getOneTimeTotal() {
    const discountAmount = Math.round(oneTimeUnitPrice * (moduleState.quantityDiscount / 100));
    const discountedUnitPrice = oneTimeUnitPrice - discountAmount;
    return discountedUnitPrice * moduleState.currentQuantity;
  }

  /**
   * Calculate one-time compare total (without discount)
   */
  function getOneTimeCompareTotal() {
    return oneTimeUnitPrice * moduleState.currentQuantity;
  }

  /**
   * Calculate subscription total
   */
  function getSubscriptionTotal() {
    const subData = getSubscriptionPriceData();
    return subData.unitPrice * moduleState.currentQuantity;
  }

  /**
   * Update one-time option price display
   */
  function updateOneTimeOptionDisplay() {
    const oneTimeOption = container.querySelector('[data-purchase-option="one-time"]');
    if (!oneTimeOption) return;

    const priceEl = oneTimeOption.querySelector('[data-onetime-price]');
    const comparePriceEl = oneTimeOption.querySelector('[data-onetime-compare-price]');

    const total = getOneTimeTotal();
    const compareTotal = getOneTimeCompareTotal();
    // Use compare_at_price if higher than one-time price, otherwise fall back to quantity discount compare
    const effectiveCompare = compareAtPrice > oneTimeUnitPrice ? compareAtPrice * moduleState.currentQuantity : compareTotal;
    const hasDiscount = moduleState.quantityDiscount > 0 || (compareAtPrice > oneTimeUnitPrice);

    if (priceEl) {
      priceEl.textContent = formatMoney(total, moneyFormat);
    }

    if (comparePriceEl) {
      if (hasDiscount) {
        comparePriceEl.textContent = formatMoney(effectiveCompare, moneyFormat);
        comparePriceEl.classList.remove('tw-hidden');
      } else {
        comparePriceEl.classList.add('tw-hidden');
      }
    }
  }

  /**
   * Update subscription option price display
   */
  function updateSubscriptionOptionDisplay() {
    const subscriptionOption = container.querySelector('[data-purchase-option="subscription"]');
    if (!subscriptionOption) return;

    const subData = getSubscriptionPriceData();
    const total = getSubscriptionTotal();
    // Subscription compares against the regular variant price (special_price_ui excluded).
    const compareTotal = regularUnitPrice * moduleState.currentQuantity;

    const priceEl = subscriptionOption.querySelector('[data-subscription-price]');
    const comparePriceEl = subscriptionOption.querySelector('[data-subscription-compare-price]');
    const badgeEl = subscriptionOption.querySelector('[data-subscription-badge]');

    if (priceEl) {
      priceEl.textContent = formatMoney(total, moneyFormat);
    }

    if (comparePriceEl) {
      if (subData.discountPercentage > 0) {
        comparePriceEl.textContent = formatMoney(compareTotal, moneyFormat);
        comparePriceEl.classList.remove('tw-hidden');
      } else {
        comparePriceEl.classList.add('tw-hidden');
      }
    }

    if (badgeEl) {
      if (subData.discountPercentage > 0) {
        badgeEl.textContent = subData.discountPercentage + '% Off';
        badgeEl.classList.remove('tw-hidden');
      } else {
        badgeEl.classList.add('tw-hidden');
      }
    }
  }

  /**
   * Update the main price block (shown above, after the description) to reflect
   * the currently selected purchase option's UNIT price:
   *  - One-time   → one-time unit price, compare = compare-at (when higher).
   *  - Subscription → subscription unit price, compare = one-time price (struck through).
   */
  function updateMainPriceDisplay() {
    const display = document.querySelector('[data-precision-price-display]');
    if (!display) return;

    const currentEl = display.querySelector('[data-current-price]');
    const compareEl = display.querySelector('[data-compare-price]');
    const isSubscription = getSelectedSellingPlan() !== '';

    let unitPrice;
    let comparePrice;
    if (isSubscription) {
      unitPrice = getSubscriptionPriceData().unitPrice;
      comparePrice = regularUnitPrice;
    } else {
      unitPrice = oneTimeUnitPrice;
      comparePrice = compareAtPrice;
    }

    if (currentEl) currentEl.textContent = formatMoney(unitPrice, moneyFormat);

    if (compareEl) {
      if (comparePrice > unitPrice) {
        compareEl.textContent = formatMoney(comparePrice, moneyFormat);
        compareEl.classList.remove('tw-hidden');
      } else {
        compareEl.classList.add('tw-hidden');
      }
    }
  }

  /**
   * Update all option displays with current state
   */
  function updateOptionDisplays() {
    updateOneTimeOptionDisplay();
    updateSubscriptionOptionDisplay();
    updateMainPriceDisplay();
  }

  /**
   * Rebuild the URL to reflect the current variant + selling plan, the same way the
   * subscription app does: keep every existing query param (e.g. `view=supplements`),
   * set `variant` from the form's hidden id input, and add `selling_plan` when a
   * subscription is selected (remove it otherwise). Uses replaceState — no navigation.
   *
   * @param {string} sellingPlanValue - Selected selling plan id ('' for one-time)
   */
  function syncUrl(sellingPlanValue) {
    const idInput = document.querySelector('input[name="id"].product-variant-id');
    const params = new URLSearchParams(window.location.search);

    if (idInput && idInput.value) {
      params.set('variant', idInput.value);
    }
    if (sellingPlanValue) {
      params.set('selling_plan', sellingPlanValue);
    } else {
      params.delete('selling_plan');
    }
    // `purchase=one-time` is consumed once at module eval (FORCE_ONE_TIME) — drop it from the
    // URL after selection so the address bar stays clean. Intent survives the session via the
    // guard, but NOT a manual refresh (no param left to counter Appstle's subscription default).
    params.delete('purchase');

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);
  }

  /**
   * Handle selection change (radio button or frequency)
   */
  function updateSelection() {
    const sellingPlanValue = getSelectedSellingPlan();
    const isSubscription = sellingPlanValue !== '';

    // Update visual selected state
    options.forEach(option => {
      const radio = option.querySelector('[data-purchase-radio]');
      if (radio && radio.checked) {
        option.classList.add('is-selected');
      } else {
        option.classList.remove('is-selected');
      }
    });

    // Update hidden selling_plan input for form submission
    if (sellingPlanInput) {
      const sellingPlans = document.querySelectorAll('[name="selling_plan"]');
      sellingPlans.forEach(item => { 
        item.value = sellingPlanValue
      })
      // sellingPlanInput.value = sellingPlanValue;
    }

    // Update option price displays
    updateOptionDisplays();

    // Swap helper text for the selected plan (Option 3)
    updateHelperText();

    // Keep the URL in sync with the selection (mirrors Appstle's updateHistoryState):
    // preserve existing params, set `variant`, and set/remove `selling_plan`.
    syncUrl(sellingPlanValue);

    // Dispatch event for preset quantity module to update per-unit price
    const subData = getSubscriptionPriceData();
    const event = new CustomEvent('selling-plan:changed', {
      detail: {
        sellingPlan: sellingPlanValue,
        isSubscription,
        subscriptionPrice: subData.unitPrice,
        oneTimePrice: oneTimeUnitPrice
      },
      bubbles: true
    });
    container.dispatchEvent(event);
  }

  // Set up radio button handlers
  radios.forEach(radio => {
    radio.addEventListener('change', updateSelection);
  });

  // Set up frequency dropdown handler
  if (frequencySelect) {
    frequencySelect.addEventListener('change', function () {
      const subscriptionRadio = container.querySelector('[data-purchase-radio][data-selling-plan]:not([data-selling-plan=""])');
      if (subscriptionRadio) {
        subscriptionRadio.checked = true;
      }
      updateSelection();
    });
  }

  // Handle click on entire option (better UX)
  options.forEach(option => {
    option.addEventListener('click', function (e) {
      if (e.target.closest('[data-frequency-select]')) {
        return;
      }

      const radio = this.querySelector('[data-purchase-radio]');
      if (radio) {
        radio.checked = true;
        updateSelection();
      }
    });
  });

  // Mark as initialized so event listeners start working
  isInitialized = true;

  // Refresh/share persistence: if the URL carried a selling_plan on load (captured at module
  // eval, before the subscription app strips it), pre-select that subscription so the page
  // matches the link. Falls back to the Liquid default when absent or unmatched.
  // One-time radio to force when the URL asked for it (null otherwise). Queried once, reused
  // by the initial selection below and the anti-race guard further down.
  const forceOneTimeRadio = FORCE_ONE_TIME
    ? container.querySelector('[data-purchase-radio][data-selling-plan=""]')
    : null;
  const selectOneTime = () => radios.forEach((radio) => { radio.checked = radio === forceOneTimeRadio; });

  if (forceOneTimeRadio) {
    selectOneTime();
  } else if (INITIAL_SELLING_PLAN) {
    if (frequencySelect) {
      const matchOption = Array.from(frequencySelect.options)
        .find((opt) => opt.value === INITIAL_SELLING_PLAN);
      if (matchOption) frequencySelect.value = INITIAL_SELLING_PLAN;
    }
    const subRadio = container.querySelector(`[data-purchase-radio][data-selling-plan="${INITIAL_SELLING_PLAN}"]`)
      || container.querySelector('[data-purchase-radio][data-selling-plan]:not([data-selling-plan=""])');
    if (subRadio) {
      radios.forEach((radio) => { radio.checked = radio === subRadio; });
    }
  }

  // Fallback: if Liquid rendered no `checked` radio (e.g. a product without selling plans,
  // where the one-time option carries no default), select the default here so the UI never
  // loads with zero options highlighted.
  if (!container.querySelector('[data-purchase-radio]:checked')) {
    const fallbackRadio = container.querySelector('[data-purchase-radio][data-selling-plan]:not([data-selling-plan=""])')
      || radios[0];
    if (fallbackRadio) fallbackRadio.checked = true;
  }

  // Initialize with current selection
  updateSelection();

  // The subscription app (Appstle) initializes AFTER us and re-writes its own hidden
  // selling_plan input + the URL back to a subscription — clobbering `?purchase=one-time`.
  // Re-assert One-Time for a short window after load so the app can't win the race. Stops
  // early once the selection stays clean. Only runs when the customer explicitly asked for
  // one-time via the URL, so it never fights a normal (subscription-default) load.
  if (forceOneTimeRadio) {
    let ticks = 0;
    const guard = setInterval(() => {
      const dirty = Array.from(document.querySelectorAll('[name="selling_plan"]'))
        .some((input) => input.value)
        || new URLSearchParams(window.location.search).has('selling_plan');
      if (dirty) {
        selectOneTime();
        updateSelection();
      }
      // ponytail: 200ms × 15 = 3s window covers Appstle's late init; extend if it lands slower.
      if (++ticks >= 15) clearInterval(guard);
    }, 200);
  }

  // Expose methods on container
  container.updateOptionDisplays = updateOptionDisplays;
  // Reset the purchase selection to the default option (Subscription when available,
  // otherwise the first radio in DOM order).
  // Called by the variant picker on variant change — mirrors the old template, which
  // re-renders the section and lands back on the default option.
  container.resetPurchaseSelection = function() {
    const defaultRadio = container.querySelector('[data-purchase-radio][data-selling-plan]:not([data-selling-plan=""])')
      || radios[0];
    if (!defaultRadio) return;
    radios.forEach(radio => { radio.checked = radio === defaultRadio; });
    updateSelection();
  };
  container.setQuantityData = function(quantity, discount) {
    moduleState.currentQuantity = quantity;
    moduleState.quantityDiscount = discount;
  };
  // Called by the variant picker when the customer selects a different variant.
  // Recomputes the one-time base + compare prices AND every subscription plan price
  // against the new variant, so a subscription stays selected and correctly priced
  // when switching variants. Selling-plan discounts are a constant percentage across
  // variants, so subscription unit price = variant price × (1 − discount%).
  container.setVariantData = function(unitPrice, comparePrice, planPrices, regularPrice) {
    oneTimeUnitPrice = parseInt(unitPrice, 10) || 0;
    compareAtPrice = parseInt(comparePrice, 10) || 0;
    // Regular price for subscription compare/discount (special_price_ui excluded from subs).
    regularUnitPrice = parseInt(regularPrice, 10) || regularUnitPrice;

    const prices = planPrices || {};

    // Prefer Shopify's authoritative per-plan allocation price for this variant; fall back to
    // the percentage estimate only when a plan id isn't present in the map.
    const repricePlan = (el, planId) => {
      if (!el) return;
      if (planId && Object.prototype.hasOwnProperty.call(prices, planId)) {
        el.dataset.planPrice = String(prices[planId]);
      } else {
        const discount = parseInt(el.dataset.discountPercentage, 10) || 0;
        el.dataset.planPrice = String(Math.round(regularUnitPrice * (1 - discount / 100)));
      }
    };

    // Subscription option pill (single plan) and, when present, each frequency option.
    const subOption = container.querySelector('[data-purchase-option="subscription"]');
    repricePlan(subOption, subOption && subOption.dataset.sellingPlanId);
    if (frequencySelect) {
      Array.from(frequencySelect.options).forEach((opt) => repricePlan(opt, opt.value));
    }
  };
}

export default initPurchaseOptions;
